import { compareVersions, validate } from "compare-versions";

export const OFFLINE_HEADSET_UPDATE_POLICY = {
  minimumVersion: "2.8.5",
  lastPermittedTestDate: {
    year: 2026,
    month: 8,
    day: 14,
  },
  updateUrl: "https://www.oculera.health/device-setup",
} as const;

export type OfflineHeadsetUpdatePolicyStatus = "allowed" | "warning" | "blocked";

type EvaluateOfflineHeadsetUpdatePolicyParams = {
  version: string | null | undefined;
  now?: Date;
  isOffline?: boolean;
};

const WARNING_SESSION_KEY = [
  "offline-headset-update-warning",
  OFFLINE_HEADSET_UPDATE_POLICY.minimumVersion,
  `${OFFLINE_HEADSET_UPDATE_POLICY.lastPermittedTestDate.year}-${OFFLINE_HEADSET_UPDATE_POLICY.lastPermittedTestDate.month}-${OFFLINE_HEADSET_UPDATE_POLICY.lastPermittedTestDate.day}`,
].join(":");

function isPastLastPermittedTestDate(now: Date): boolean {
  const { year, month, day } = OFFLINE_HEADSET_UPDATE_POLICY.lastPermittedTestDate;
  const firstBlockedDate = new Date(year, month - 1, day + 1);
  return now >= firstBlockedDate;
}

export function evaluateOfflineHeadsetUpdatePolicy({
  version,
  now = new Date(),
  isOffline = process.env.REACT_APP_OFFLINE === "true",
}: EvaluateOfflineHeadsetUpdatePolicyParams): OfflineHeadsetUpdatePolicyStatus {
  if (!isOffline) {
    return "allowed";
  }

  const isSupported =
    typeof version === "string" &&
    validate(version) &&
    compareVersions(version, OFFLINE_HEADSET_UPDATE_POLICY.minimumVersion) >= 0;

  if (isSupported) {
    return "allowed";
  }

  return isPastLastPermittedTestDate(now) ? "blocked" : "warning";
}

export function formatOfflineHeadsetUpdateDeadline(locale?: string): string {
  const { year, month, day } = OFFLINE_HEADSET_UPDATE_POLICY.lastPermittedTestDate;
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(year, month - 1, day));
}

export function hasOfflineHeadsetUpdateWarningBeenShown(): boolean {
  try {
    return sessionStorage.getItem(WARNING_SESSION_KEY) === "true";
  } catch {
    return false;
  }
}

export function markOfflineHeadsetUpdateWarningAsShown(): void {
  try {
    sessionStorage.setItem(WARNING_SESSION_KEY, "true");
  } catch {
    // The warning can safely repeat if session storage is unavailable.
  }
}

export function openOfflineHeadsetUpdatePage(): void {
  window.open(OFFLINE_HEADSET_UPDATE_POLICY.updateUrl, "_blank", "noopener,noreferrer");
}
